import clsx from "clsx";
import { type JSX, useCallback, useEffect, useRef, useState } from "react";

import Confirm, { type ConfirmProps } from "./common/confirm";
import Modal from "./common/modal";

const MENU_WIDTH = 200;

export type ContextMenuOption = {
  icon: string;
  label: string;
  onClick?: () => void;
  form?: JSX.Element;
  // The menu row itself becomes the trigger, so the caller leaves out children.
  confirm?: Omit<ConfirmProps, "children">;
};

interface ContextMenuProps {
  options: ContextMenuOption[];
  right?: boolean;
}

export default function ContextMenu({ options, right }: ContextMenuProps) {
  const _menuwrapperref = useRef<HTMLDivElement>(null);

  const [menu, setMenu] = useState({ is_open: false, left: 0, top: 0 });

  const outerrowclass = clsx("group", "w-full", "cursor-pointer");
  const innerrowclass = clsx(
    "flex",
    "items-center",
    "group-hover:text-slate-600",
    "hover:bg-slate-300",
    "text-sm",
    "px-2",
    "py-1",
  );
  const iconclass = clsx("material-symbols-rounded", "mr-2");
  const menuclass = clsx(
    "absolute",
    "w-44",
    "flex",
    "flex-col",
    "z-50",
    "bg-slate-800",
    "rounded-md",
    {
      hidden: !menu.is_open,
    },
  );

  const _toggleOpen = useCallback(() => {
    if (!_menuwrapperref.current) return;
    const { width, height } = _menuwrapperref.current.getBoundingClientRect();
    setMenu((state) => {
      return {
        is_open: !state.is_open,
        left: right ? 0 : width - MENU_WIDTH + 24,
        top: height,
      };
    });
  }, [right]);

  const _handleKeydown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        _toggleOpen();
        ev.preventDefault();
      }
    },
    [_toggleOpen],
  );

  const _handleClickOutside = useCallback(
    (ev: MouseEvent) => {
      if (
        _menuwrapperref.current &&
        ev.target instanceof Node &&
        !_menuwrapperref.current.contains(ev.target)
      ) {
        ev.preventDefault();
        _toggleOpen();
      }
    },
    [_toggleOpen],
  );

  // The listeners only exist while the menu is open, which is why _handleKeydown
  // no longer re-checks is_open for itself.
  useEffect(() => {
    if (!menu.is_open) return;
    document.addEventListener("keydown", _handleKeydown);
    document.addEventListener("click", _handleClickOutside);
    return () => {
      document.removeEventListener("keydown", _handleKeydown);
      document.removeEventListener("click", _handleClickOutside);
    };
  }, [menu.is_open, _handleKeydown, _handleClickOutside]);

  const _handleMenuClick = () => {
    setMenu((state) => {
      return { ...state, is_open: false };
    });
  };

  if (options.length === 0) return null;

  return (
    <div className="relative h-[24px] font-normal" ref={_menuwrapperref}>
      <button
        type="button"
        className={clsx(
          "material-symbols-rounded",
          "z-0",
          "cursor-pointer",
          "select-none",
          "rounded-full",
          "active:bg-slate-700",
          "hover:text-slate-600",
        )}
        onClick={_toggleOpen}
        aria-expanded={menu.is_open}
        aria-label="More actions"
      >
        more_vert
      </button>
      <div className={menuclass} style={{ left: menu.left, top: menu.top }} role="menu">
        {options.map((option, idx) => {
          let inner_row: JSX.Element;
          let innerrowclass_override = innerrowclass;
          let outerrowclass_override = outerrowclass;
          if (idx === 0) {
            // the first row
            innerrowclass_override = clsx(innerrowclass, "rounded-t-md");
            outerrowclass_override = clsx(outerrowclass, "rounded-t-md");
          }
          if (idx === options.length - 1) {
            // the last row
            innerrowclass_override = clsx(innerrowclass, "rounded-b-md");
            outerrowclass_override = clsx(outerrowclass, "rounded-b-md");
          }
          if (option.form) {
            inner_row = (
              <Modal onOpen={_handleMenuClick}>
                <button
                  type="button"
                  role="menuitem"
                  className={clsx(innerrowclass_override, "w-full text-left")}
                >
                  <span className={iconclass}>{option.icon}</span>
                  <span>{option.label}</span>
                </button>
                {option.form}
              </Modal>
            );
          } else if (option.confirm) {
            const { onOpen: confirmOnOpen, ...confirm_props } = option.confirm;
            inner_row = (
              <Confirm
                {...confirm_props}
                onOpen={() => {
                  confirmOnOpen?.();
                  _handleMenuClick();
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className={clsx(innerrowclass_override, "w-full text-left")}
                >
                  <span className={iconclass}>{option.icon}</span>
                  <span>{option.label}</span>
                </button>
              </Confirm>
            );
          } else {
            inner_row = (
              <button
                type="button"
                role="menuitem"
                className={clsx(innerrowclass_override, "w-full text-left")}
                onClick={() => {
                  option.onClick?.();
                  _handleMenuClick();
                }}
              >
                <span className={iconclass}>{option.icon}</span>
                <span>{option.label}</span>
              </button>
            );
          }
          return (
            <div key={option.icon} className={outerrowclass_override}>
              {inner_row}
            </div>
          );
        })}
      </div>
    </div>
  );
}
